import React from "react";

/**
 * CommonButton - Reusable button using existing CSS classes from style.css.
 *
 * Props:
 *   children    {node}     Button label / content
 *   variant     {string}   "primary" | "outline" | "danger" | "outline-danger" | "muted"
 *                            primary       => btn-primary  (from style.css)
 *                            outline       => btn-outline-primary (from style.css)
 *                            danger        => btn-danger (from style.css)
 *                            outline-danger=> btn-outline-danger (from style.css)
 *                            muted         => btn-secondary (from style.css)
 *   onClick     {function}
 *   disabled    {boolean}
 *   type        {string}   "button" | "submit" | "reset" (default: "button")
 *   className   {string}   Extra classes
 *   style       {object}   Extra style overrides
 *   minWidth    {string}   Min width override (default: "auto")
 *   height      {string}   Height override (default: "45px")
 *   size        {string}   "default" | "xs" | "sm" | "lg" — controls padding/shape
 *                            default => rounded-pill px-4 fw-bold
 *                            xs      => btn-xs
 *                            sm      => btn-sm rounded-pill px-3 fw-bold
 *                            lg      => btn-lg rounded-pill px-5 fw-bold
 */
const CommonButton = ({
  children,
  variant = "primary",
  onClick,
  disabled = false,
  type = "button",
  className = "",
  style = {},
  minWidth = "auto",
  height = "45px",
  size = "default",
}) => {
  // Map variant names to Bootstrap CSS classes from style.css
  const variantClassMap = {
    primary: "btn-primary",
    outline: "btn-outline-primary",
    danger: "btn-danger",
    "outline-danger": "btn-outline-danger",
    success: "btn-success",
    "outline-success": "btn-outline-success",
    warning: "btn-warning",
    "outline-warning": "btn-outline-warning",
    info: "btn-info",
    "outline-info": "btn-outline-info",
    muted: "btn-secondary",
    light: "btn-light",
  };

  const btnClass = variantClassMap[variant] || variantClassMap.primary;

  const sizeClassMap = {
    default: "rounded-pill px-4 fw-bold",
    xs: "btn-xs px-4",
    sm: "btn-sm rounded-pill px-3 fw-bold",
    lg: "btn-lg rounded-pill px-5 fw-bold",
  };

  const sizeClass = sizeClassMap[size] || sizeClassMap.default;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`btn ${btnClass} ${sizeClass} ${className}`}
      style={{
        height,
        minWidth,
        whiteSpace: "nowrap",
        fontSize: "14px",
        ...style,
      }}
    >
      {children}
    </button>
  );
};

export default CommonButton;
